import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useLogin } from '@/lib/api/auth';
import OracyLogo from '@/components/OracyLogo';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, Eye, EyeOff, CheckCircle, Shield } from 'lucide-react';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Get the intended destination or default to admin dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/admin';
  const registrationMessage = (location.state as { message?: string })?.message;

  const loginMutation = useLogin();

  // Show registration success message if present
  useEffect(() => {
    if (registrationMessage) {
      setSuccess(registrationMessage);
    }
  }, [registrationMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await loginMutation.mutateAsync({ email, password });
      
      // Check if user needs onboarding
      const user = response.user;
      if (!user.is_verified || !user.role) {
        setError('Your account is not set up. Please complete onboarding first.');
        return;
      }
      
      // Check if user is admin
      if (user.role !== 'admin') {
        setError('Access denied. This login is for administrators only.');
        return;
      }
      
      // Redirect to admin dashboard
      navigate('/admin', { replace: true });
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <OracyLogo variant="full" theme="dark" />
        </div>

        <Card className="w-full shadow-xl border-slate-700 bg-slate-800/50">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Admin Login</CardTitle>
            <CardDescription className="text-slate-300">
              Secure access to the Oracy administration panel
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {/* Success Message */}
              {success && (
                <div className="flex items-center gap-2 p-3 text-sm text-green-400 bg-green-900/20 rounded-lg border border-green-800/50">
                  <CheckCircle className="w-4 h-4" />
                  {success}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-900/20 rounded-lg border border-red-800/50">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-slate-300">
                  Admin Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@oracy.ai"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loginMutation.isPending}
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-slate-300">
                    Password
                  </label>
                  <Link
                    to="/admin/forgot-password"
                    className="text-sm text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loginMutation.isPending}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in as Admin'
                )}
              </Button>
              
              <div className="text-center text-sm text-slate-400">
                Need admin access?{' '}
                <Link to="/admin/contact" className="text-primary hover:underline">
                  Contact IT Support
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>
        
        <div className="mt-6 text-center text-sm text-slate-500">
          <Link to="/login" className="hover:text-slate-300">
            ← Back to regular login
          </Link>
        </div>
      </motion.div>
    </div>
  );
}